import React, { useMemo, useCallback, useState } from 'react';
import { Avatar, CircularProgress } from '@mui/material';

interface AvatarCellProps {
  avatar: string;
  name: string;
  dimensionType: string;
  onCircleClick: (row: any) => void;
  onPersonClick: (id: string) => void;
  rowData: any;
}

const AvatarCell = React.memo<AvatarCellProps>(
  ({ avatar, name, dimensionType, onCircleClick, onPersonClick, rowData }) => {
    const [isLoading, setIsLoading] = useState(false);

    const initials = useMemo(() => {
      if (!name) return '';
      return name
        .split(' ')
        .map((word) => word.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }, [name]);

    const handleClick = useCallback(async () => {
      setIsLoading(true);
      try {
        if (dimensionType === 'circle') {
          onCircleClick(rowData);
        } else {
          onPersonClick(rowData.id);
        }
      } finally {
        // Reset loading state after a short delay to show feedback
        setTimeout(() => setIsLoading(false), 500);
      }
    }, [dimensionType, onCircleClick, onPersonClick, rowData]);

    return (
      <div
        onClick={handleClick}
        style={{
          cursor: isLoading ? 'wait' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          opacity: isLoading ? 0.7 : 1,
          transition: 'opacity 0.2s ease',
        }}
      >
        <Avatar
          src={avatar || undefined}
          alt={name[0]}
          sx={{
            width: 40,
            height: 40,
            fontSize: '16px',
            backgroundColor: avatar ? 'transparent' : 'grey',
            color: avatar ? 'inherit' : 'white',
          }}
        >
          {!avatar && initials}
        </Avatar>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {name}
          {isLoading && (
            <CircularProgress size={16} sx={{ color: '#ff6a00' }} />
          )}
        </div>
      </div>
    );
  }
);

AvatarCell.displayName = 'AvatarCell';

export default AvatarCell;
